/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useLocale } from "@i18n/react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { radius, spacing } from "../../../../../global/emotion";
import { Button } from "../Button/Button.emotion";
import { Mask } from "../Mask/Mask.emotion";

const cssButton: Interpolation = {
  borderRadius: radius.s,
  padding: `${spacing.none} ${spacing.ms}`,
};

const cssButtonInactive: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});

const cssYearLabel: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
  padding: `${spacing.none} ${spacing.s}`,
  borderRadius: radius.s,
  flexShrink: 0,
});

const cssMonthSelectorRoot: Interpolation = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: spacing.xxs,
};

const cssYearNav: Interpolation = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingInline: spacing.ms,
};

const cssScrollRoot: Interpolation = {
  position: "relative",
  width: "100%",
};

const cssMonthSelectorScroll: Interpolation = {
  display: "flex",
  overflowX: "auto",
  gap: spacing.s,
  padding: `${spacing.xxs} ${spacing.ms}`,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  alignItems: "center",
  height: "48px",
};

const cssMaskLeft: Interpolation = {
  position: "absolute",
  top: 0,
  bottom: 0,
  left: 0,
};

const cssMaskRight: Interpolation = {
  position: "absolute",
  top: 0,
  bottom: 0,
  right: 0,
};

const cssYearGrid: Interpolation<ThemeType> = (theme) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 50,
  background: theme.semanticColors.background,
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: spacing.xxs,
  padding: `${spacing.xxs} ${spacing.ms}`,
});

const cssYearGridItem: Interpolation = {
  borderRadius: radius.s,
  width: "100%",
  justifyContent: "center",
  padding: `${spacing.xxs} 0`,
};

type MonthItem =
  | { type: "month"; date: Date; key: string }
  | { type: "year"; year: number; key: string };

const RANGE = 5;
const PICK_RANGE = 12;

function buildMonthsAroundYear(centerYear: number): MonthItem[] {
  const items: MonthItem[] = [];
  for (let year = centerYear - RANGE; year <= centerYear + RANGE; year++) {
    items.push({ type: "year", year, key: `year-${year}` });
    for (let month = 0; month < 12; month++) {
      items.push({
        type: "month",
        date: new Date(year, month, 1),
        key: `${year}-${month}`,
      });
    }
  }
  return items;
}

type Props = {
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
};

export function MonthSelector({ currentMonth, onMonthChange }: Props) {
  const activeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);
  const [yearPickerOpen, setYearPickerOpen] = useState(false);
  const locale = useLocale();

  useEffect(() => {
    const button = activeRef.current;
    const container = scrollRef.current;
    if (!button || !container) return;

    const behavior: ScrollBehavior = isMounted.current ? "smooth" : "instant";
    isMounted.current = true;

    const containerCenter = container.offsetWidth / 2;
    const buttonCenter = button.offsetLeft + button.offsetWidth / 2;
    container.scrollTo({ left: buttonCenter - containerCenter, behavior });
  }, [currentMonth]);

  useEffect(() => {
    if (yearPickerOpen) return;
    const button = activeRef.current;
    const container = scrollRef.current;
    if (!button || !container) return;
    const containerCenter = container.offsetWidth / 2;
    const buttonCenter = button.offsetLeft + button.offsetWidth / 2;
    container.scrollTo({
      left: buttonCenter - containerCenter,
      behavior: "instant",
    });
  }, [yearPickerOpen]);

  const currentYear = currentMonth.getFullYear();
  const monthItems = useMemo(
    () => buildMonthsAroundYear(currentYear),
    [currentYear],
  );
  const pickYears = useMemo(
    () =>
      Array.from(
        { length: PICK_RANGE * 2 + 1 },
        (_, i) => currentYear - PICK_RANGE + i,
      ),
    [currentYear],
  );

  return (
    <div css={cssMonthSelectorRoot}>
      <div css={cssYearNav}>
        <Button
          size="s"
          variant="transparentNeutral"
          onClick={() =>
            onMonthChange(new Date(currentYear - 1, currentMonth.getMonth(), 1))
          }
        >
          ‹
        </Button>
        <Button
          size="s"
          variant="transparentNeutral"
          onClick={() => setYearPickerOpen((v) => !v)}
        >
          {`${currentYear} ${yearPickerOpen ? "▴" : "▾"}`}
        </Button>
        <Button
          size="s"
          variant="transparentNeutral"
          onClick={() =>
            onMonthChange(new Date(currentYear + 1, currentMonth.getMonth(), 1))
          }
        >
          ›
        </Button>
      </div>

      <div css={cssScrollRoot}>
        <Mask
          size="xs"
          color="background"
          direction="to right"
          orientation="vertical"
          css={cssMaskLeft}
        />
        <div ref={scrollRef} css={cssMonthSelectorScroll}>
          {monthItems.map((item) => {
            if (item.type === "year") {
              return (
                <Button
                  key={item.key}
                  size="s"
                  variant="transparentNeutral"
                  css={cssYearLabel as any}
                  onClick={() =>
                    onMonthChange(
                      new Date(item.year, currentMonth.getMonth(), 1),
                    )
                  }
                >
                  {`${item.year}`}
                </Button>
              );
            }

            const isActive =
              item.date.getFullYear() === currentMonth.getFullYear() &&
              item.date.getMonth() === currentMonth.getMonth();

            return (
              <Button
                key={item.key}
                ref={isActive ? activeRef : undefined}
                onClick={() => onMonthChange(item.date)}
                size="s"
                variant={isActive ? "primary" : "outline"}
                css={[cssButton, !isActive && cssButtonInactive] as any}
              >
                {item.date.toLocaleString(locale, { month: "short" })}
              </Button>
            );
          })}
        </div>
        <Mask
          size="xs"
          color="background"
          direction="to left"
          orientation="vertical"
          css={cssMaskRight}
        />
        {yearPickerOpen && (
          <div css={cssYearGrid}>
            {pickYears.map((year) => (
              <Button
                key={year}
                size="s"
                variant={year === currentYear ? "primary" : "outline"}
                css={
                  [
                    cssYearGridItem,
                    year !== currentYear && cssButtonInactive,
                  ] as any
                }
                onClick={() => {
                  onMonthChange(new Date(year, currentMonth.getMonth(), 1));
                  setYearPickerOpen(false);
                }}
              >
                {`${year}`}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
